import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import { PICKING_SESSION_STATUS_COLORS } from '../constants/orderStatuses';
import { formatDuration, formatDateTime } from '../utils/formatters';
import DateRangePicker from '../components/DateRangePicker';
import { usePickerStats } from '../hooks/usePickerStats';

export default function PickerStats() {
  const {
    dateRange, setDateRange,
    pickers, loading, error,
    selectedPicker, setSelectedPicker,
    detailSessions, detailLoading,
    openDetail,
  } = usePickerStats();

  if (selectedPicker) {
    return (
        <div className="space-y-6">
          <button
              onClick={() => setSelectedPicker(null)}
              className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to all pickers
          </button>

          <header>
            <h1 className="text-2xl font-semibold text-stone-900">
              {selectedPicker.picker_first_name} {selectedPicker.picker_last_name}
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              {dateRange?.from?.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
              {dateRange?.to && dateRange.to !== dateRange.from ? ` — ${dateRange.to.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
            </p>
          </header>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Assigned" value={selectedPicker.assigned} />
            <StatCard label="Completed" value={selectedPicker.completed} accent="emerald" />
            <StatCard label="Cancelled" value={selectedPicker.cancelled} accent="red" />
            <StatCard label="Avg Items/Min" value={selectedPicker.avg_items_per_minute ?? '—'} />
          </div>

          <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
            <div className="px-5 py-3 border-b border-stone-200">
              <h2 className="font-semibold text-stone-900">Session History</h2>
            </div>
            {detailLoading ? (
                <div className="px-4 py-10 text-center text-sm text-stone-500">Loading…</div>
            ) : detailSessions.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-stone-500">No sessions in this range.</div>
            ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Started</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Items</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Missing</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                    {detailSessions.map((s) => (
                        <tr key={s.picking_session_id} className="hover:bg-stone-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-stone-900">{s.order_reference}</td>
                          <td className="px-4 py-3">
                            <StatusBadge status={s.session_status} colorMap={PICKING_SESSION_STATUS_COLORS} />
                          </td>
                          <td className="px-4 py-3 text-stone-600 text-xs">{formatDateTime(s.started_at)}</td>
                          <td className="px-4 py-3 text-stone-600">{formatDuration(s.pick_duration_seconds)}</td>
                          <td className="px-4 py-3 text-stone-600">{s.items_scanned}/{s.items_required}</td>
                          <td className="px-4 py-3 text-stone-600">
                            {s.missing_count > 0 ? (
                                <span className="text-amber-700 font-medium">{s.missing_count}</span>
                            ) : '—'}
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
            )}
          </section>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-stone-900">Picker Stats</h1>
          <p className="text-sm text-stone-500 mt-1">Productivity and accuracy across all pickers.</p>
        </header>

        <div className="flex items-center gap-3">
          <DateRangePicker align="left" value={dateRange} onChange={setDateRange} />
        </div>

        {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
        )}

        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900">All Pickers</h2>
            <span className="text-sm text-stone-500">{pickers.length} total</span>
          </div>

          {loading ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">Loading…</div>
          ) : pickers.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">No picking activity in this range.</div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Picker</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Assigned</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Completed</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Cancelled</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Items Scanned</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Missing</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Avg Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Avg Items/Min</th>
                    <th className="px-4 py-3" />
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {pickers.map((p) => (
                      <tr key={p.picker_id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-stone-900">{p.picker_first_name} {p.picker_last_name}</td>
                        <td className="px-4 py-3 text-stone-600">{p.assigned}</td>
                        <td className="px-4 py-3 text-emerald-700 font-medium">{p.completed}</td>
                        <td className="px-4 py-3 text-red-600 font-medium">{p.cancelled}</td>
                        <td className="px-4 py-3 text-stone-600">{p.total_items_scanned}</td>
                        <td className="px-4 py-3 text-stone-600">
                          {p.total_missing > 0 ? <span className="text-amber-700 font-medium">{p.total_missing}</span> : '—'}
                        </td>
                        <td className="px-4 py-3 text-stone-600">{formatDuration(p.avg_pick_duration_seconds)}</td>
                        <td className="px-4 py-3 text-stone-600">{p.avg_items_per_minute ?? '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                              onClick={() => openDetail(p)}
                              className="rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-[#ee2b74] hover:bg-[#d4245f] transition"
                          >
                            View Detail
                          </button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>
      </div>
  );
}